<template>
  <v-card outlined>
    <v-card-title class="align-start pb-2">
      <span class="text-subtitle-1 font-weight-bold">Comparatif IA / Humains</span>
      <v-spacer />
      <v-progress-circular
        v-if="loading"
        indeterminate
        size="18"
        width="2"
        color="secondary"
      />
      <dot-menu
        :isLoading="exporting"
        @exportExcel="$emit('exportExcel')"
        :excelButton="{
          excelData: excelData,
          filename: excelFilename,
          disabled: exporting,
        }"
      >
        <template #extraButtons>
          <v-list-item
            link
            :disabled="exporting || exportingAnnual"
            @click="showAnnualModal = true"
          >
            <v-list-item-title>Export Excel annuel</v-list-item-title>
          </v-list-item>
        </template>
      </dot-menu>

      <v-dialog v-model="showAnnualModal" max-width="400px">
        <v-card>
          <v-card-title class="text-h6">
            Export Excel annuel (Comparatif IA / Humains)
          </v-card-title>
          <v-card-text class="pt-3">
            <v-select
              v-model="selectedYear"
              :items="yearOptions"
              label="Sélectionnez l'année"
              outlined
              dense
            />
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn text @click="showAnnualModal = false">Annuler</v-btn>
            <v-btn color="primary" @click="confirmAnnualExport">
              Exporter
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-title>

    <v-card-text v-if="hasData">
      <v-alert
        v-if="noAudience"
        dense
        outlined
        type="warning"
        class="caption mb-3"
      >
        Aucune donnée d'audience rattachée aux articles sur cette période
        (table <code>adn_post_ga</code>). Les volumes d'articles ci-dessous
        restent valables, mais les sessions et le CA ne sont pas exploitables.
      </v-alert>

      <v-simple-table dense>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left"></th>
              <th class="text-right">Articles</th>
              <th class="text-right">Sessions</th>
              <th class="text-right">CA</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.key">
              <td class="font-weight-medium">
                {{ row.label }}
              </td>
              <td class="text-right">
                {{ row.nbPosts }}
                <span v-if="row.nbPostsShare" class="text--secondary caption">
                  {{ row.nbPostsShare }}
                </span>
              </td>
              <td class="text-right">
                {{ row.sessions }}
                <span v-if="row.sessionsShare" class="text--secondary caption">
                  {{ row.sessionsShare }}
                </span>
              </td>
              <td class="text-right">
                {{ row.ca }}
                <span v-if="row.caShare" class="text--secondary caption">
                  {{ row.caShare }}
                </span>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <p class="text--secondary caption mb-0 mt-3">
        Parts calculées sur IA + Humains. Total site :
        {{ siteSessions }} sessions &middot; {{ siteCa }}. Articles mesurés :
        {{ articlesSessions }} sessions &middot; {{ articlesCa }}.
      </p>
      <p class="text--secondary caption mb-0">
        Le CA par article est une ventilation au prorata des sessions (RPM du site),
        pas un CA mesuré article par article.
      </p>
      <p class="text--secondary caption mb-0">
        L'export Excel du menu ci-dessus sort une ligne par site pour
        <b>tous les sites ayant des articles IA</b>, sur la plage de dates
        sélectionnée — il ne tient pas compte du filtre site de la page.
      </p>
    </v-card-text>

    <v-card-text v-else class="text--secondary">
      Aucune donnée sur la période sélectionnée.
    </v-card-text>
  </v-card>
</template>

<script>
import { computed, ref } from "@vue/composition-api";

import { formatCurrency, formatNumber, formatPercent } from "@/utils/formatting";

import DotMenu from "@/components/common/menus/DotMenu";

export default {
  name: "IaVsHumanCard",
  components: {
    DotMenu,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    exporting: {
      type: Boolean,
      default: false,
    },
    exportingAnnual: {
      type: Boolean,
      default: false,
    },
    excelData: {
      type: Blob,
      default: () => new Blob([""], { type: "text/plain" }),
    },
    excelFilename: {
      type: String,
      default: "comparatif-ia-humains.xlsx",
    },
  },
  setup(props, { emit }) {
    const bucket = (name) => props.data[name] || {};

    const hasData = computed(
      () => Boolean(props.data && props.data.articles)
    );

    const noAudience = computed(() => {
      if (!hasData.value) {
        return false;
      }
      const articles = bucket("articles");
      return !articles.sessions && !articles.ca;
    });

    const share = (value, total) =>
      total > 0 ? `(${formatPercent(value / total)})` : "";

    const audienceCell = (value, formatter) =>
      noAudience.value ? "—" : formatter(value || 0);

    const rows = computed(() => {
      if (!hasData.value) {
        return [];
      }

      const ia = bucket("ia");
      const human = bucket("human");

      const totals = {
        nbPosts: (ia.nb_posts || 0) + (human.nb_posts || 0),
        sessions: (ia.sessions || 0) + (human.sessions || 0),
        ca: (ia.ca || 0) + (human.ca || 0),
      };

      const attributed = (label, key, values) => ({
        key,
        label,
        nbPosts: formatNumber(values.nb_posts || 0),
        nbPostsShare: share(values.nb_posts || 0, totals.nbPosts),
        sessions: audienceCell(values.sessions, formatNumber),
        sessionsShare: share(values.sessions || 0, totals.sessions),
        ca: audienceCell(values.ca, formatCurrency),
        caShare: share(values.ca || 0, totals.ca),
      });

      return [
        attributed("IA", "ia", ia),
        attributed("Humains", "human", human),
      ];
    });

    const orDash = (value, formatter) => (value ? formatter(value) : "—");

    const siteSessions = computed(() =>
      orDash(bucket("site").sessions, formatNumber)
    );
    const siteCa = computed(() => orDash(bucket("site").ca, formatCurrency));
    const articlesSessions = computed(() =>
      orDash(bucket("articles").sessions, formatNumber)
    );
    const articlesCa = computed(() =>
      orDash(bucket("articles").ca, formatCurrency)
    );

    const currentYear = new Date().getFullYear();
    const showAnnualModal = ref(false);
    const selectedYear = ref(currentYear);
    const yearOptions = computed(() => {
      const years = [];
      for (let y = currentYear; y >= 2020; y--) {
        years.push(y);
      }
      return years;
    });

    const confirmAnnualExport = () => {
      showAnnualModal.value = false;
      emit("exportAnnualExcel", selectedYear.value);
    };

    return {
      hasData,
      noAudience,
      rows,
      siteSessions,
      siteCa,
      articlesSessions,
      articlesCa,
      showAnnualModal,
      selectedYear,
      yearOptions,
      confirmAnnualExport,
    };
  },
};
</script>

<style scoped></style>
